import React, { forwardRef, useEffect, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { CalendarBlank, TrashSimple, X } from '@phosphor-icons/react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import moment from 'moment'
import Select from 'react-select'
import { User } from '../../types/User'

export interface TimeOffFormData {
  startDate: string
  endDate: string
  description: string
  userId?: number
}

interface InitialValues {
  startDate: Date | string
  endDate: Date | string
  description: string
}

interface LogTimeOffModalProps {
  show: boolean
  onClose: () => void
  onSubmit: (data: TimeOffFormData) => Promise<boolean | void> | boolean | void
  users?: User[]
  mode?: 'create' | 'edit'
  initialValues?: InitialValues
  onDelete?: () => void
}

const DatePickerInput = forwardRef<
  HTMLInputElement,
  { value?: string; onClick?: () => void; placeholder?: string }
>(({ value, onClick, placeholder }, ref) => (
  <div className="log-modal__date-wrapper">
    <input
      ref={ref}
      value={value || ''}
      onClick={onClick}
      readOnly
      placeholder={placeholder || 'MM/DD/YYYY'}
      className="log-modal__date-input"
    />
    <span className="log-modal__date-icon">
      <CalendarBlank size={14} weight="regular" />
    </span>
  </div>
))
DatePickerInput.displayName = 'DatePickerInput'

function countWorkDays(start: Date | null, end: Date | null): number {
  if (!start || !end || end < start) return 0
  let count = 0
  const cur = new Date(start)
  while (cur <= end) {
    const day = cur.getDay()
    if (day !== 0 && day !== 6) count++
    cur.setDate(cur.getDate() + 1)
  }
  return count
}

const toDate = (value: Date | string) => moment(value).startOf('day').toDate()

export const LogTimeOffModal = ({
  show,
  onClose,
  onSubmit,
  users,
  mode = 'create',
  initialValues,
  onDelete,
}: LogTimeOffModalProps) => {
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [notes, setNotes] = useState('')
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isEditMode = mode === 'edit'
  const isAdminMode = Boolean(users)

  // Only hydrate when the modal opens. Depending on `initialValues` would
  // reset the form to stale parent state while a save is in flight.
  useEffect(() => {
    if (!show) {
      setIsSubmitting(false)
      return
    }

    if (mode === 'edit' && initialValues) {
      setStartDate(toDate(initialValues.startDate))
      setEndDate(toDate(initialValues.endDate))
      setNotes(initialValues.description ?? '')
      setSelectedUserId(null)
      return
    }

    if (mode === 'create') {
      setStartDate(null)
      setEndDate(null)
      setNotes('')
      setSelectedUserId(null)
    }
  }, [show, mode])

  const userOptions = useMemo(
    () =>
      (users ?? [])
        .map(u => ({
          value: u.id,
          label: `${u.firstName} ${u.lastName}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [users],
  )

  const workDays = useMemo(
    () => countWorkDays(startDate, endDate),
    [startDate, endDate],
  )

  const resetForm = () => {
    setStartDate(null)
    setEndDate(null)
    setNotes('')
    setSelectedUserId(null)
  }

  const handleClose = () => {
    if (isSubmitting) return
    resetForm()
    onClose()
  }

  const canSave =
    startDate !== null &&
    endDate !== null &&
    (!isAdminMode || selectedUserId !== null)

  const handleSubmit = async () => {
    if (!canSave || !startDate || !endDate || isSubmitting) return
    setIsSubmitting(true)
    try {
      const result = await onSubmit({
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD'),
        description: notes,
        ...(isAdminMode && selectedUserId !== null
          ? { userId: selectedUserId }
          : {}),
      })
      if (result === false) return
      if (!isEditMode) resetForm()
    } finally {
      setIsSubmitting(false)
    }
  }

  const title = isEditMode
    ? 'Edit Time Off'
    : isAdminMode
    ? 'Add Time Off'
    : 'Log Time Off'

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      dialogClassName="log-modal-dialog"
      contentClassName="log-modal-content"
    >
      <div className={`log-modal${isAdminMode ? ' log-modal--admin' : ''}`}>
        <div className="log-modal__header">
          <h2 className="log-modal__title">{title}</h2>
          <button
            className="log-modal__close"
            onClick={handleClose}
            aria-label="Close"
          >
            <X size={20} weight="regular" />
          </button>
        </div>

        <div className="log-modal__content">
          <div className="log-modal__fields">
            {isAdminMode && (
              <div className="log-modal__field">
                <label className="log-modal__label log-modal__label--required">
                  Team Member
                </label>
                <Select
                  options={userOptions}
                  onChange={opt => setSelectedUserId(opt?.value ?? null)}
                  placeholder="Search team member..."
                  classNamePrefix="log-modal-select"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 1060 }),
                  }}
                />
              </div>
            )}

            <div className="log-modal__date-row">
              <div className="log-modal__field">
                <label className="log-modal__label log-modal__label--required">
                  Start Date
                </label>
                <DatePicker
                  selected={startDate}
                  onChange={date => {
                    setStartDate(date)
                    if (endDate && date && date > endDate) setEndDate(null)
                  }}
                  selectsStart
                  startDate={startDate ?? undefined}
                  endDate={endDate ?? undefined}
                  maxDate={endDate ?? undefined}
                  dateFormat="MM/dd/yyyy"
                  customInput={<DatePickerInput />}
                  placeholderText="MM/DD/YYYY"
                  popperProps={{ strategy: 'fixed' }}
                />
              </div>
              <div className="log-modal__field">
                <label className="log-modal__label log-modal__label--required">
                  End Date
                </label>
                <DatePicker
                  selected={endDate}
                  onChange={date => setEndDate(date)}
                  selectsEnd
                  startDate={startDate ?? undefined}
                  endDate={endDate ?? undefined}
                  minDate={startDate ?? undefined}
                  dateFormat="MM/dd/yyyy"
                  customInput={<DatePickerInput />}
                  placeholderText="MM/DD/YYYY"
                  popperProps={{ strategy: 'fixed' }}
                />
              </div>
            </div>

            <div className="log-modal__day-counter">
              <CalendarBlank size={14} weight="regular" color="#717182" />
              {workDays} {workDays === 1 ? 'day' : 'days'}
            </div>

            <div className="log-modal__notes-field">
              <label className="log-modal__label">Notes</label>
              <textarea
                className="log-modal__notes"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Add any notes for your reviewer..."
              />
            </div>

            {isEditMode && onDelete && (
              <button
                type="button"
                className="log-modal__delete-link"
                onClick={onDelete}
                disabled={isSubmitting}
              >
                <TrashSimple size={14} weight="regular" />
                Delete this time off entry
              </button>
            )}
          </div>

          <div className="log-modal__footer">
            <button
              className="log-modal__btn log-modal__btn--cancel"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              className="log-modal__btn log-modal__btn--save"
              onClick={handleSubmit}
              disabled={!canSave || isSubmitting}
            >
              {isAdminMode ? 'Add Time Off' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default LogTimeOffModal
